import { FileRepository } from "@/components/features";
import {
  Form,
  FormSubmitButton,
  TextFormField,
  Typography,
} from "@/components/ui";
import { AppModal } from "@/components/ui/AppModal/AppModal";
import { FormCancelButton } from "@/components/ui/FormFields/FormCancelButton/FormCancelButton";
import { retrieveMassTortQueryOptions } from "@/lib/data/react-query/mass-torts/mass-tort";
import { retrieveMatterQueryOptions } from "@/lib/data/react-query/matters/matter";
import {
  retrieveMatterFilesQueryOptions,
  useMergeMatterFiles,
} from "@/lib/data/react-query/matters/matter-file";
import type { MatterFileParentInput } from "@/lib/data/requests/matters/matter-file";
import type { MassTort, Matter, MatterFile } from "@/lib/types";
import { isMergeSupportedMatterFile } from "@/lib/utils/matterFile";
import { zodResolver } from "@hookform/resolvers/zod";
import { useSuspenseQueries, useSuspenseQuery } from "@tanstack/react-query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { z } from "zod";

export interface MergeDocumentModalProps {
  open: boolean;
  setOpen: (open: boolean) => void;
  matterId?: string;
  massTortId?: string;
  initialSelectedFileIds?: string[];
}

const formSchema = z.object({
  mergedFileName: z.string().min(1, {
    message: "Please enter a file name",
  }),
  selectedFiles: z.array(z.string()).min(2, {
    message: "Please select at least two files",
  }),
});

const defaultObject: z.infer<typeof formSchema> = {
  mergedFileName: "",
  selectedFiles: [],
};

type MergeDocumentModalBodyProps = {
  open: boolean;
  setOpen: (open: boolean) => void;
  mergeParent: MatterFileParentInput;
  repositoryMatter?: Matter;
  repositoryMassTort?: MassTort;
  mergeContextDescription: string;
  initialSelectedFileIds?: string[];
};

function MergeDocumentModalBody({
  open,
  setOpen,
  mergeParent,
  repositoryMatter,
  repositoryMassTort,
  mergeContextDescription,
  initialSelectedFileIds,
}: MergeDocumentModalBodyProps) {
  const normalizedParentMatterId = useMemo(
    () => repositoryMassTort?.id.trim() ?? repositoryMatter?.id ?? "",
    [repositoryMassTort?.id, repositoryMatter?.id],
  );

  const matterFiles = useMemo<MatterFile[]>(() => {
    const raw = repositoryMatter?.files ?? repositoryMassTort?.files ?? [];
    return raw.map((file) => ({
      ...file,
      matterId: file.matterId ?? normalizedParentMatterId,
      idLocation: file.idLocation ?? "",
      presignedUrlKey: file.presignedUrlKey ?? undefined,
      variables: file.variables ?? undefined,
    })) as MatterFile[];
  }, [
    normalizedParentMatterId,
    repositoryMatter?.files,
    repositoryMassTort?.files,
  ]);

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    values: defaultObject,
    mode: "onSubmit",
  });
  const [selectedFiles, setSelectedFiles] = useState<MatterFile[]>([]);
  const mergeMatterFiles = useMergeMatterFiles();

  const getMergeEligibleFilesByIds = useCallback(
    (fileIds: string[]) =>
      matterFiles.filter(
        (file) => fileIds.includes(file.id) && isMergeSupportedMatterFile(file),
      ),
    [matterFiles],
  );

  const updateSelectedFiles = useCallback(
    (nextSelectedFiles: MatterFile[]) => {
      const currentIds = new Set(selectedFiles.map((file) => file.id));
      const nextIds = new Set(nextSelectedFiles.map((file) => file.id));
      const isSameSelection =
        currentIds.size === nextIds.size &&
        Array.from(nextIds).every((id) => currentIds.has(id));

      if (isSameSelection) return;

      setSelectedFiles(nextSelectedFiles);
      form.setValue(
        "selectedFiles",
        nextSelectedFiles.map((file) => file.id),
        { shouldDirty: true },
      );
    },
    [form, selectedFiles],
  );

  const handleMergeDocument = async (data: z.infer<typeof formSchema>) => {
    await mergeMatterFiles
      .mutateAsync({
        ...mergeParent,
        files: selectedFiles.map((file) => file.id),
        name: `${data.mergedFileName}.pdf`,
      })
      .then(() => {
        setOpen(false);
      });
  };

  const handleExternalSelectionChange = useCallback(
    (fileIds: string[], checked: boolean, replaceSelection = false) => {
      const nextSelectedFiles = replaceSelection
        ? getMergeEligibleFilesByIds(fileIds)
        : checked
          ? [
              ...selectedFiles,
              ...getMergeEligibleFilesByIds(fileIds).filter(
                (file) =>
                  !selectedFiles.some(
                    (selectedFile) => selectedFile.id === file.id,
                  ),
              ),
            ]
          : selectedFiles.filter((file) => !fileIds.includes(file.id));

      updateSelectedFiles(nextSelectedFiles);
    },
    [getMergeEligibleFilesByIds, selectedFiles, updateSelectedFiles],
  );

  useEffect(() => {
    if (!open) {
      form.reset(defaultObject);
      setSelectedFiles([]);
    }
  }, [open, form]);

  useEffect(() => {
    if (!open) return;
    if (!initialSelectedFileIds || initialSelectedFileIds.length === 0) {
      return;
    }
    if (selectedFiles.length > 0) return;
    const preselected = getMergeEligibleFilesByIds(initialSelectedFileIds);
    if (preselected.length === 0) return;
    setSelectedFiles(preselected);
    form.setValue(
      "selectedFiles",
      preselected.map((file) => file.id),
      { shouldDirty: true },
    );
  }, [
    form,
    getMergeEligibleFilesByIds,
    initialSelectedFileIds,
    open,
    selectedFiles,
  ]);

  return (
    <Form {...form}>
      <AppModal
        open={open}
        onOpenChange={setOpen}
        size="3xl"
        title="Merge document"
        description={mergeContextDescription}
        isDirty={form.formState.isDirty}
        bodyContained
        loading={mergeMatterFiles.isPending}
        footer={
          <div className="flex justify-end gap-2">
            <FormCancelButton
              setOpen={setOpen}
              isDirty={form.formState.isDirty}
            />
            <FormSubmitButton
              submitFormId="merge-document-form"
              loading={mergeMatterFiles.isPending}
              title={`Save (${selectedFiles.length})`}
            />
          </div>
        }
      >
        <form
          id="merge-document-form"
          className="relative flex h-full min-h-0 flex-col overflow-hidden"
          onSubmit={async (e) => {
            e.preventDefault();
            e.stopPropagation();
            await form.handleSubmit(handleMergeDocument)();
          }}
        >
          <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
            <fieldset className="grid grid-cols-1 gap-4 px-6 pt-4 md:grid-cols-2">
              <TextFormField
                register={form.register("mergedFileName")}
                label="Merged file name"
                placeholder="Enter a file name"
              />
              <Typography variant="subHeading" className="mb-3 self-end">
                .pdf
              </Typography>
            </fieldset>
            <div className="flex min-h-0 flex-1 flex-col px-6 pb-4">
              <Typography className="text-destructive text-xs font-medium">
                {form.getFieldState("selectedFiles").error?.message}
              </Typography>
              <div className="mt-2 flex min-h-0 flex-1 overflow-hidden">
                <FileRepository
                  matter={repositoryMatter}
                  massTort={repositoryMassTort}
                  useExternalSelection
                  onExternalSelectionChange={handleExternalSelectionChange}
                  selectedFiles={selectedFiles}
                  enabledFileTypes={["pdf", "jpg", "png", "jpeg"]}
                  showMergeDocuments={false}
                  showShareFolders={false}
                  showSelectionActions={false}
                  enableTreeDrag={false}
                />
              </div>
            </div>
          </div>
        </form>
      </AppModal>
    </Form>
  );
}

function MergeDocumentModalForMatter({
  matterId,
  ...rest
}: Omit<MergeDocumentModalProps, "massTortId"> & { matterId: string }) {
  const { data: matterData } = useSuspenseQuery(
    retrieveMatterQueryOptions(matterId),
  );
  const matter = matterData.data;

  return (
    <MergeDocumentModalBody
      mergeParent={{ matterId }}
      repositoryMatter={matter}
      mergeContextDescription="Merge documents for this matter"
      {...rest}
    />
  );
}

function MergeDocumentModalForMassTort({
  massTortId,
  ...rest
}: Omit<MergeDocumentModalProps, "matterId"> & { massTortId: string }) {
  const [{ data: massTortData }, { data: matterFilesResponse }] =
    useSuspenseQueries({
      queries: [
        retrieveMassTortQueryOptions(massTortId),
        retrieveMatterFilesQueryOptions({ massTortId }),
      ],
    });

  const base = massTortData.data;
  const files = matterFilesResponse.data;

  const massTortWithFiles: MassTort = {
    ...base,
    matterFile: files.map((file) => ({
      ...file,
      massTortId,
    })),
  };

  return (
    <MergeDocumentModalBody
      mergeParent={{ massTortId }}
      repositoryMassTort={massTortWithFiles}
      mergeContextDescription="Merge documents for this mass tort"
      {...rest}
    />
  );
}

export const MergeDocumentModal = ({
  matterId,
  massTortId,
  ...rest
}: MergeDocumentModalProps) => {
  if (matterId) {
    return <MergeDocumentModalForMatter matterId={matterId} {...rest} />;
  }
  if (massTortId) {
    return <MergeDocumentModalForMassTort massTortId={massTortId} {...rest} />;
  }
  return null;
};
